import { getSdk } from "./sdkService";
import { i18n } from "./i18nService";
import { config } from "../config/config";
import { getGlobalButtonInfo } from "../errors/globalButtons";
import { historyMethods } from "../utils/historyMethods";
import { isNonChangeableMop } from "../utils/isNonChangeableMop";
import { redirectTo } from "./navigationService";
import { routes } from "../routes";
import { showSingleModalButton } from "./modalsService";
import { getAppState } from "../state";
import { getPaymentMethodByPlanId } from "../services/paymentService";

const INTERNAL = "internal";
const EXTERNAL = "external";
const APPLE_IAP = "apple_iap";
const UNMANAGED_OPERATOR = "unmanagedOperator";

/**
 * Uses the SDK to perform an activate request whether to submit or update a user's payment method.
 * In case of being successful, the app will be notified so that the app UI is updated accordingly.
 * @param {Object} paymentData - Payment data object.
 * @returns {Promise}
 * @public
 */
export async function activate(paymentData) {
  try {
    const sdk = await getSdk();
    return await sdk.billingManager.activate(paymentData);
  } catch (error) {
    throw error;
  }
}

export async function getAllActiveSubscriptionsDeepQuery() {
  try {
    const result = await getSdk().userManager.getAllActiveSubscriptionsDeepQuery();
    return result;
  } catch (error) {
    throw error;
  }
}
/**
 * Uses the SDK to perform a request to retrieve user's payments information.
 * @returns {Promise}
 * @public
 */
export async function getUserPayments(isAvoidCache = true) {
  try {
    const result = await getSdk().userManager.getBilling({ avoidCache: isAvoidCache });
    return result;
  } catch (error) {
    throw error;
  }
}

export async function getBillingInfo(isAvoidCache = false) {
  const userBillingInfo = getAppState().userBilling;
  if (userBillingInfo) {
    return userBillingInfo;
  } else {
    const response = await getUserPayments(isAvoidCache);
    storeBillingInfo(response);
  }
}

export function storeBillingInfo(billingInfo) {
  const appState = getAppState();
  appState.userBilling = billingInfo;
}
/**
 * Checks current active user payment method to retrieve a label to identify the text
 * to be showed in the warning modal when going to redeem a voucher.
 * @param {Object} userPaymentMethod - User's active payment method.
 */
export function getUserPaymentMethodWarningLabel(userPaymentMethod) {
  const isUnmanagedPayment = config.get().unmanagedPaymentPlans.filter(payment => payment === userPaymentMethod);
  if (!!userPaymentMethod && isUnmanagedPayment.length) {
    const isApplePay = isUnmanagedPayment[0] === APPLE_IAP;
    const isUnmanagedOperator = !isApplePay ? `${EXTERNAL}.${UNMANAGED_OPERATOR}` : `${EXTERNAL}.${APPLE_IAP}`;
    const unmanagedOperatorLabel = `${i18n.get(`pages.voucherRedemption.warning.${isUnmanagedOperator}`)}${i18n.get(
      `pages.voucherRedemption.warning.${EXTERNAL}.reminder`
    )}`;
    return {
      modalText: unmanagedOperatorLabel,
      buttons: showSingleModalButton("accept", () => {
        this.props.stateActions.hideGlobalModal();
        redirectTo.call(this, routes.home, null, historyMethods.REPLACE);
      })
    };
  }
  // This will be reached in case user account is Active through a subscription but not through a payment method.
  return {
    modalText: i18n.get(`pages.voucherRedemption.warning.${INTERNAL}`),
    buttons: [
      {
        ...getGlobalButtonInfo("continue"),
        buttonHandler: () => {
          this.props.stateActions.hideGlobalModal();
          this.redeemVoucher();
        }
      },
      {
        ...getGlobalButtonInfo("cancel"),
        buttonHandler: this.closeModal
      }
    ]
  };
}

export async function checkForUmanagedOrNonChangeableMop(retrying = false) {
  const { stateActions } = this.props;
  const billing = await getUserPayments();
  !retrying && stateActions.setUserBilling(billing);
  let path = "";

  if (
    !billing ||
    (Array.isArray(billing) && !billing.length) ||
    !billing.subscriptions ||
    !isSubscriptionActive(billing)
  ) {
    return retrying ? false : path;
  }
  if (!isSubscriptionInfoInBilling(billing)) {
    const { parentPlanId } = billing;
    //const paymentMethod = await getPaymentMethod(parentPlanId);
    if (parentPlanId === 0) {
      return retrying ? false : path;
    }
    const paymentMethod = await getPaymentMethodByPlanId.call(this, parentPlanId);
    if (billing.subscriptions?.[0] && paymentMethod) {
      const billingInfo = Object.assign({}, billing.subscriptions[0].info ?? {}, {
        ...paymentMethod[parentPlanId]
      });
      billing.subscriptions[0].info = [billingInfo];
    }
  }
  if (isNonChangeableMop(billing)) {
    if (retrying) return true;
    path = routes.nonchangeable;
  }

  return retrying ? false : path;
}

export function isSubscriptionInfoInBilling(billing) {
  return billing.subscriptions && billing.subscriptions.length && billing.subscriptions[0].info;
}
export function isSubscriptionActive(billing) {
  return (
    billing && billing.subscriptions && billing.subscriptions.length && billing.subscriptions[0].state === "Active"
  );
}

export async function checkIsNonChangeableMop(retrying = false, addon) {
  const userAddonsBillingInfo = await getUserPaymentPlansFromAddonsBilling.call(this, addon);
  return userAddonsBillingInfo;
}

export async function getUserPaymentPlansFromAddonsBilling(addon) {
  const userBillingInfo = getAppState().userSwitchPlaninfo;

  /*if (userBillingInfo) { // removed userBillingInfo from global state
      return userBillingInfo;
    } else {*/
  const addonBillings = await getSubscriptionsStatus(); // user addons deep call
  const billing = addonBillings.filter(data => data?.subscriptionName === addon)[0];

  const billingInfo = billing?.paymentMethod?.paymentMethodParams;
  let mergedUserAddonsPlusMOP = billingInfo;
  const planId =
    billing?.paymentMethod?.paymentMethodParams?.parentPlanId || billing?.paymentMethod?.paymentMethodParams?.planId;
  if (planId) {
    const mop = await getPaymentMethodByPlanId(planId);
    mergedUserAddonsPlusMOP = { ...billingInfo, ...mop[planId] };
    getAppState().userSwitchPlaninfo = mergedUserAddonsPlusMOP;
  }

  return mergedUserAddonsPlusMOP;
  //}
}

export async function getSubscriptionsStatus() {
  try {
    const sdk = await getSdk();
    return await sdk.userManager.getSubscriptionsStatus();
  } catch (error) {
    throw error;
  }
}
